import React from 'react';
import classNames from 'classnames/bind';
import { CellProps, Column } from 'react-table';
import { useToggle } from '@mantine/hooks';

import { Camera, getCameraStatusHelpText } from 'types/camera';
import { Gateway } from 'types/gateway';

import { relativeTimeFromNow } from 'services/date';
import { useHasAccess } from 'hooks/useHasAccess';
import { useLinkedCameras } from 'gateways/hooks/useLinkedCameras';

import { AddCameraDialog } from 'cameras/components/AddCameraDialog';
import { Button } from 'components/Button/Button';
import { NameCell, Table } from 'components/Table/Table';
import { StatusIndicator } from 'components/StatusIndicator/StatusIndicator';
import { Text } from 'components/Text/Text';

import styles from './CamerasTable.module.scss';

const c = classNames.bind(styles);

export type GatewayCamerasProps = {
  gateway: Gateway;
};

const columns: Column<Camera>[] = [
  {
    Header: 'Name',
    Cell(props: CellProps<Camera>) {
      return (
        <NameCell
          {...props}
          to={`/deploy/cameras/${props.row.original.id}`}
          entity="camera"
        />
      );
    },
  },
  {
    Header: 'Status',
    Cell(camera: CellProps<Camera>) {
      return (
        <StatusIndicator
          status={camera.row.original.status}
          tooltip={getCameraStatusHelpText(camera.row.original)}
        />
      );
    },
  },
  {
    Header: 'Model',
    accessor(camera) {
      return camera.model || '';
    },
  },
  {
    Header: 'Address',
    accessor(camera) {
      return camera.ip_local || '';
    },
  },
  {
    Header: 'Recent activity',
    accessor(camera) {
      return relativeTimeFromNow(camera.updated_at);
    },
  },
];

export function GatewayCameras({ gateway }: GatewayCamerasProps) {
  const { data: linkedCameras, isLoading } = useLinkedCameras(gateway.id);

  const data = React.useMemo(
    () => (linkedCameras ? linkedCameras.data : []),
    [linkedCameras]
  );

  const [isAddingCamera, toggleIsAddingCamera] = useToggle();

  const [hasAccess] = useHasAccess();

  return (
    <div className={c('wrap')}>
      <header className={c('header')}>
        <Text type="paragraph">
          ONVIF and USB cameras accessible by this gateway
        </Text>

        {hasAccess('deploy_edit') && (
          <>
            <Button
              id="up-gateway-cameras_add"
              onClick={() => toggleIsAddingCamera(true)}
              variant="primary"
              size="small"
            >
              Add Camera
            </Button>
            <AddCameraDialog
              open={isAddingCamera}
              onClose={toggleIsAddingCamera}
              gateway={gateway}
            />
          </>
        )}
      </header>

      <Table
        id="gateway-cameras-overview"
        label="gateway cameras"
        columns={columns}
        data={data}
        loading={isLoading}
      />
    </div>
  );
}
